import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';
import { IUbicacionBase } from 'modelos/src';

/**
 * HospitalesLayerManager
 *
 * Manager para renderizar hospitales en el mapa.
 *
 * Características:
 * - Icono simple de hospital
 * - Sin clustering (los hospitales no se agrupan)
 * - Icono: hospital.svg (35x35px)
 */
@Injectable({
  providedIn: 'root',
})
export class HospitalesLayerManager extends BaseEntityLayerManager<
  IUbicacionBase<'Hospital'>
> {
  // ========== CONFIGURACIÓN ==========
  readonly entityType = 'hospitales';
  readonly supportsClustering = false; // Los hospitales no se agrupan
  readonly clusterColor = '#E91E63'; // No usado, pero requerido por la base

  // IDs de layers
  private mainLayerId = 'hospitales-layer';
  private textLayerId = 'hospitales-text-layer';

  // Variable para tracking del ID seleccionado
  private idHospitalSeleccionado: string | null = null;

  // ========== RENDERIZACIÓN ==========

  /**
   * Convierte un hospital a un Feature de GeoJSON
   */
  override entityToFeature(
    hospital: IUbicacionBase<'Hospital'>,
  ): GeoJSON.Feature<GeoJSON.Point> {
    // OPTIMIZACIÓN: Almacenar en mapa en vez de serializar
    this.storeEntity(hospital);

    // Los hospitales tienen ubicacion.geojson con las coordenadas
    let coordinates: [number, number];

    if (hospital?.geojson?.coordinates) {
      coordinates = hospital.geojson.coordinates as [number, number];
    } else {
      // Fallback: Buenos Aires
      console.warn(`⚠️ Hospital ${hospital._id} sin coordenadas válidas`);
      coordinates = [-58.443832, -34.587386];
    }

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties: {
        id: hospital._id,
        identificacion: hospital.identificacion,
        telefono: hospital.valores?.telefono,
        email: hospital.valores?.email,
        direccion: hospital?.direccion,
        // Datos completos para popup
      },
    };
  }

  /**
   * Icono de hospital
   */
  override getIconExpression(): string {
    return 'hospital-icon';
  }

  // ========== CREACIÓN DE LAYERS ==========

  /**
   * Crea los layers para hospitales
   */
  protected override createLayers(): void {
    if (!this.map) return;

    // Layer principal de hospitales
    this.addLayer({
      id: this.mainLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        visibility: 'visible',
        'icon-image': this.getIconExpression(),
        'icon-size': 0.583, // SVG 60px → 35px (35/60 = 0.583)
        'icon-allow-overlap': true,
        'icon-padding': 0,
        'icon-rotation-alignment': 'viewport',
        'icon-pitch-alignment': 'viewport',
        'icon-anchor': 'bottom', // Anclar en la parte inferior como pins
      },
    });

    // Text layer para mostrar identificación del hospital seleccionado
    this.addLayer({
      id: this.textLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'text-field': ['get', 'identificacion'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-anchor': 'top',
        'text-offset': [0, 1.5],
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        visibility: 'none', // Inicialmente oculto
      },
      paint: {
        'text-color': '#000000',
        'text-halo-color': '#FFFFFF',
        'text-halo-width': 2,
      },
    });
  }

  // ========== MOSTRAR NOMBRE ==========

  /**
   * Muestra u oculta el nombre de un hospital específico
   * @param hospitalId ID del hospital o null para ocultar todos los nombres
   */
  public mostrarNombreDeHospital(hospitalId: string | null): void {
    if (!this.map || !this.map.getLayer(this.textLayerId)) return;

    this.idHospitalSeleccionado = hospitalId;

    if (hospitalId === null) {
      // Ocultar todos los nombres
      this.map.setLayoutProperty(this.textLayerId, 'visibility', 'none');
      this.map.setFilter(this.textLayerId, null);
    } else {
      // Mostrar solo el nombre del hospital seleccionado
      this.map.setLayoutProperty(this.textLayerId, 'visibility', 'visible');
      this.map.setFilter(this.textLayerId, ['==', ['get', 'id'], hospitalId]);
    }
  }

  // ========== VISIBILIDAD GENERAL ==========

  override setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;

    if (this.map.getLayer(this.mainLayerId)) {
      this.map.setLayoutProperty(
        this.mainLayerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
    if (this.map.getLayer(this.textLayerId)) {
      this.map.setLayoutProperty(
        this.textLayerId,
        'visibility',
        visible && this.idHospitalSeleccionado ? 'visible' : 'none',
      );
    }
  }
}
